import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { MdPrint, MdRefresh, MdRemove, MdAdd } from "react-icons/md";

import axios from "../axiosConfig";
import { lineGoesWithCasket } from "../productCategories";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { brandFor } from "../businessBranding";
import { useActiveBusiness } from "../auth/useActiveBusiness";
import { useBranding } from "../theme/BrandingProvider";
import { replaceCharacter } from "../components";
import fallbackLogo from "../images/logo.png";
import "../work-orders-print.css";

/* ------------------------------------------------------------------ *
 * Work order day sheet (print) — /workorders/print/:filter
 *
 * Replaces the "workorders" branch of pages/Print.js. Same URL contract
 * as before, so the list pages (new and legacy) keep opening it:
 *   :filter = JSON { filter: { funeral_date | delivery_date: "DD-MM-YYYY",
 *                              type: "Funeral" | "Retail" } }
 *
 * Data comes from the same endpoints the old sheet used —
 * POST /wo/getAllBy/ for the day's orders and GET /settings/getAll for
 * the casket-cover product types. Funeral rows are merged per funeral
 * (parent_funeral_id) exactly as before, so a funeral with several work
 * orders prints as one line with all its products pooled.
 *
 * The column set is 1:1 with the legacy sheet; only the styling and the
 * toolbar (font size, paper size, live summary) are new.
 * ------------------------------------------------------------------ */

const PAPERS = [
  { id: "a3-landscape", label: "A3 landscape", page: "A3 landscape" },
  { id: "a4-landscape", label: "A4 landscape", page: "A4 landscape" },
  { id: "a4-portrait", label: "A4 portrait", page: "A4 portrait" },
];
const FONT_MIN = 9;
const FONT_MAX = 18;
const FONT_DEFAULT = 12;
const STORAGE_KEY = "rf-wop-prefs";

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};
const locName = (raw) => (safeParse(raw, {}) || {}).name || "";

/* "CC302 x 1", "Wreath x 3" — casket covers keyed by code, everything
   else by product type, amounts summed. Same grouping as the list page. */
function groupProducts(list) {
  const order = [];
  const byKey = {};
  (list || []).forEach((p) => {
    const type = String(p.product_type || "");
    const key =
      type.toLowerCase() === "casket cover"
        ? p.code || type
        : type || p.product_name || p.name || "Product";
    if (!byKey[key]) {
      byKey[key] = { key, amount: 0, isCC: type.toLowerCase() === "casket cover" };
      order.push(key);
    }
    byKey[key].amount += Number(p.amount) || 1;
  });
  return order.map((k) => byKey[k]);
}

function readPrefs() {
  try {
    return safeParse(localStorage.getItem(STORAGE_KEY), {}) || {};
  } catch (e) {
    return {};
  }
}
function writePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    /* private mode — preferences just don't persist */
  }
}

/* Funeral: one line per funeral, products pooled across its work orders. */
function buildFuneralRows(orders, ccTypes) {
  const groups = [];
  const byFuneral = {};
  (orders || []).forEach((o) => {
    const key = o.parent_funeral_id || o._id;
    if (!byFuneral[key]) {
      byFuneral[key] = [];
      groups.push(key);
    }
    byFuneral[key].push(o);
  });
  /* by product type, as the work order list groups them — a line added as
     a tribute (`as_tribute`) stays one */
  const withCasket = (p) => lineGoesWithCasket(p, ccTypes);
  return groups.map((key) => {
    const list = byFuneral[key];
    const first = list[0];
    let all = [];
    list.forEach((o) => {
      all = all.concat(
        safeParse(o.casket_cover_selected, []) || [],
        safeParse(o.tributes_selected, []) || []
      );
    });
    return {
      id: key,
      recipient: first.late_person_name || "",
      fdCode: (safeParse(first.fd_code, {}) || {}).code || "",
      location: locName(first.funeral_location),
      time: first.funeral_time || "",
      ccLocation: locName(first.casket_cover_delivery_location),
      ccTime: first.casket_cover_delivery_time || "",
      casketCovers: groupProducts(all.filter(withCasket)),
      tributes: groupProducts(all.filter((p) => !withCasket(p))),
      orderCount: list.length,
    };
  });
}

function buildRetailRows(orders) {
  return (orders || []).map((o) => ({
    id: o._id,
    company: o.company_name || "",
    orderedBy: o.ordered_by || "",
    recipient: o.recipient_name || "",
    location: locName(o.delivery_location),
    time: o.time_of_delivery || "",
    products: groupProducts(safeParse(o.products_selected, []) || []),
  }));
}

const sumAmounts = (rows, field, only = () => true) =>
  rows.reduce(
    (n, r) => n + r[field].filter(only).reduce((m, p) => m + p.amount, 0),
    0
  );

function Chips({ items, tone }) {
  if (!items || items.length === 0) return <span className="wop-empty">—</span>;
  return (
    <span className="wop-chips">
      {items.map((p) => (
        <span key={p.key} className={"wop-chip " + (tone || "")}>
          <span className="wop-chip-name">{p.key}</span>
          <span className="wop-chip-n">×{p.amount}</span>
        </span>
      ))}
    </span>
  );
}

const Cell = ({ children, className }) => (
  <td className={className}>{children === "" || children == null ? <span className="wop-empty">—</span> : children}</td>
);

export default function WorkOrdersPrintPage() {
  const { filter: rawFilter } = useParams();
  const { business: tokenBusiness, user } = useAuth();
  /* Name, logo and palette follow the business that is open — the same
     one the shell and the :root theme use — falling back to the token's. */
  const activeBusiness = useActiveBusiness();
  const business =
    activeBusiness && (activeBusiness.name || activeBusiness.code)
      ? activeBusiness
      : tokenBusiness || activeBusiness;
  useBranding(business); // re-render when its logo or palette is saved

  const request = useMemo(() => {
    const parsed = safeParse(decodeURIComponent(rawFilter || ""), null) || safeParse(rawFilter, {});
    const f = (parsed && parsed.filter) || {};
    const funeral = f.type !== "Retail";
    const hyphenDate = funeral ? f.funeral_date : f.delivery_date;
    const slashDate = replaceCharacter(hyphenDate || "", "-", "/");
    return {
      valid: Boolean(slashDate),
      funeral,
      type: funeral ? "Funeral" : "Retail",
      slashDate,
      body: {
        ...parsed,
        filter: funeral
          ? { ...f, type: "Funeral", funeral_date: slashDate }
          : { ...f, type: "Retail", delivery_date: slashDate },
      },
    };
  }, [rawFilter]);

  const prefs = useMemo(readPrefs, []);
  const [font, setFont] = useState(() =>
    Math.min(FONT_MAX, Math.max(FONT_MIN, Number(prefs.font) || FONT_DEFAULT))
  );
  const [paper, setPaper] = useState(() =>
    PAPERS.some((p) => p.id === prefs.paper) ? prefs.paper : PAPERS[0].id
  );
  useEffect(() => {
    writePrefs({ font, paper });
  }, [font, paper]);

  const [orders, setOrders] = useState([]);
  const [ccTypes, setCcTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    axios
      .get(BASE_SERVER_URL + "/settings/getAll")
      .then((doc) => {
        if (doc.data.status === "success") {
          const s = doc.data.response[0] || {};
          const types = safeParse(s.cc_types, null);
          if (Array.isArray(types)) setCcTypes(types);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!request.valid) {
      setLoading(false);
      setError("This link has no date in it. Open the sheet from the work orders page.");
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError("");
    axios
      .post(BASE_SERVER_URL + "/wo/getAllBy/", request.body)
      .then((doc) => {
        if (!alive) return;
        if (doc.data.status === "success") setOrders(doc.data.response || []);
        else setError(doc.data.message || "The server could not load this day.");
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "Could not reach the server. Check your connection and try again."
        );
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [request, reloadTick]);

  const rows = useMemo(
    () => (request.funeral ? buildFuneralRows(orders, ccTypes) : buildRetailRows(orders)),
    [orders, ccTypes, request.funeral]
  );

  const day = moment(request.slashDate, "DD/MM/YYYY", true);
  const dateLong = day.isValid() ? day.format("dddd D MMMM YYYY") : request.slashDate || "—";
  const dateShort = day.isValid() ? day.format("ddd D MMM YYYY") : request.slashDate || "—";
  const brand = brandFor(business);
  const businessName = (business && business.name) || "Ray's Florist";
  const printedBy = (user && (user.name || user.full_name || user.username || user.email)) || "";
  const paperDef = PAPERS.find((p) => p.id === paper) || PAPERS[0];

  const stats = request.funeral
    ? [
        { k: "Funerals", v: rows.length },
        { k: "Work orders", v: orders.length },
        /* Singles, petals and the rest ride in the casket-cover column
           (cc_types) but aren't casket covers — only real ones count. */
        { k: "Casket covers", v: sumAmounts(rows, "casketCovers", (g) => g.isCC) },
        { k: "Tributes", v: sumAmounts(rows, "tributes") },
      ]
    : [
        { k: "Orders", v: rows.length },
        { k: "Products", v: sumAmounts(rows, "products") },
      ];

  useEffect(() => {
    document.title = `${request.type} work orders — ${dateShort}`;
  }, [request.type, dateShort]);

  const colgroup = request.funeral ? (
    <colgroup>
      <col className="wop-c-num" />
      <col style={{ width: "13%" }} />
      <col style={{ width: "10%" }} />
      <col style={{ width: "21%" }} />
      <col style={{ width: "8%" }} />
      <col style={{ width: "15%" }} />
      <col style={{ width: "8%" }} />
      <col style={{ width: "12%" }} />
      <col style={{ width: "13%" }} />
    </colgroup>
  ) : (
    <colgroup>
      <col className="wop-c-num" />
      <col style={{ width: "15%" }} />
      <col style={{ width: "12%" }} />
      <col style={{ width: "15%" }} />
      <col style={{ width: "26%" }} />
      <col style={{ width: "9%" }} />
      <col style={{ width: "23%" }} />
    </colgroup>
  );

  const head = request.funeral ? (
    <tr>
      <th className="wop-num">#</th>
      <th>Recipient</th>
      <th>FD code</th>
      <th>Delivery location</th>
      <th className="wop-center">Funeral service time</th>
      <th>CC delivery</th>
      <th className="wop-center">CC delivery time</th>
      <th>Casket covers</th>
      <th>Tributes</th>
    </tr>
  ) : (
    <tr>
      <th className="wop-num">#</th>
      <th>Company name</th>
      <th>Ordered by</th>
      <th>Recipient name</th>
      <th>Delivery location</th>
      <th className="wop-center">Delivery time</th>
      <th>Selected products</th>
    </tr>
  );

  const body = request.funeral
    ? rows.map((r, i) => (
        <tr key={r.id}>
          <td className="wop-num">{i + 1}</td>
          <td className="wop-strong">
            {r.recipient || <span className="wop-empty">—</span>}
            {r.orderCount > 1 && (
              <span className="wop-sub">{r.orderCount} work orders</span>
            )}
          </td>
          <Cell>{r.fdCode}</Cell>
          <Cell>{r.location}</Cell>
          <Cell className="wop-center wop-time">{r.time}</Cell>
          <Cell>{r.ccLocation}</Cell>
          <Cell className="wop-center wop-time">{r.ccTime}</Cell>
          <td><Chips items={r.casketCovers} tone="is-cc" /></td>
          <td><Chips items={r.tributes} /></td>
        </tr>
      ))
    : rows.map((r, i) => (
        <tr key={r.id}>
          <td className="wop-num">{i + 1}</td>
          <Cell className="wop-strong">{r.company}</Cell>
          <Cell>{r.orderedBy}</Cell>
          <Cell>{r.recipient}</Cell>
          <Cell>{r.location}</Cell>
          <Cell className="wop-center wop-time">{r.time}</Cell>
          <td><Chips items={r.products} /></td>
        </tr>
      ));

  const colCount = request.funeral ? 9 : 7;

  return (
    <div className={"wop-page is-" + paper}>
      <style>{`@page { size: ${paperDef.page}; margin: 9mm 10mm; }`}</style>

      <header className="wop-toolbar no-print">
        <div className="wop-tb-title">
          <span className="wop-tb-kicker">Print · {request.type} day sheet</span>
          <span className="wop-tb-date">{dateLong}</span>
        </div>

        <div className="wop-tb-stats">
          {stats.map((s) => (
            <span key={s.k} className="wop-stat">
              <span className="wop-stat-v">{loading ? "…" : s.v}</span>
              <span className="wop-stat-k">{s.k}</span>
            </span>
          ))}
        </div>

        <div className="wop-tb-controls">
          <label className="wop-ctrl">
            <span className="wop-ctrl-k">Text size</span>
            <span className="wop-stepper">
              <button
                type="button"
                aria-label="Smaller text"
                disabled={font <= FONT_MIN}
                onClick={() => setFont((f) => Math.max(FONT_MIN, f - 1))}
              >
                <MdRemove />
              </button>
              <span className="wop-stepper-v">{font}</span>
              <button
                type="button"
                aria-label="Larger text"
                disabled={font >= FONT_MAX}
                onClick={() => setFont((f) => Math.min(FONT_MAX, f + 1))}
              >
                <MdAdd />
              </button>
            </span>
          </label>

          <label className="wop-ctrl">
            <span className="wop-ctrl-k">Paper</span>
            <span className="wop-segmented" role="radiogroup" aria-label="Paper size">
              {PAPERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={paper === p.id}
                  className={paper === p.id ? "is-on" : ""}
                  onClick={() => setPaper(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </span>
          </label>

          <button
            type="button"
            className="wop-btn is-ghost"
            onClick={() => setReloadTick((t) => t + 1)}
            disabled={loading}
            title="Reload the day's orders"
          >
            <MdRefresh /> Refresh
          </button>
          <button
            type="button"
            className="wop-btn is-primary"
            onClick={() => window.print()}
            disabled={loading || Boolean(error)}
          >
            <MdPrint /> Print
          </button>
        </div>
      </header>

      <main className="wop-sheet" style={{ "--wop-font": font + "px" }}>
        <div className="wop-head">
          <div className="wop-brand">
            <img
              src={brand.logo || fallbackLogo}
              alt={businessName}
              className="wop-logo"
            />
          </div>
          <div className="wop-head-mid">
            <h1 className="wop-title">Work Order List</h1>
            <span className="wop-subtitle">
              {request.type} orders · {businessName}
            </span>
          </div>
          <div className="wop-head-date">
            <span className="wop-date-dow">{day.isValid() ? day.format("dddd") : ""}</span>
            <span className="wop-date-main">
              {day.isValid() ? day.format("D MMMM YYYY") : request.slashDate || "—"}
            </span>
          </div>
        </div>

        <div className="wop-summary">
          {stats.map((s) => (
            <span key={s.k} className="wop-summary-item">
              <span className="wop-summary-k">{s.k}</span>
              <span className="wop-summary-v">{loading ? "…" : s.v}</span>
            </span>
          ))}
        </div>

        <table className="wop-table">
          {colgroup}
          <thead>{head}</thead>
          <tbody>
            {loading ? (
              [0, 1, 2, 3, 4].map((i) => (
                <tr key={i} className="wop-skeleton">
                  <td colSpan={colCount}>
                    <span className="wop-skel" />
                  </td>
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={colCount} className="wop-state is-error">
                  <strong>Nothing to print.</strong> {error}
                  {request.valid && (
                    <button
                      type="button"
                      className="wop-btn is-ghost no-print"
                      onClick={() => setReloadTick((t) => t + 1)}
                    >
                      <MdRefresh /> Try again
                    </button>
                  )}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="wop-state">
                  No {request.type.toLowerCase()} work orders for {dateLong}.
                </td>
              </tr>
            ) : (
              body
            )}
          </tbody>
        </table>

        <footer className="wop-foot">
          <span>
            {businessName} · {request.type} work orders · {dateShort}
          </span>
          <span>
            Printed {moment().format("D MMM YYYY, h:mm a")}
            {printedBy ? ` by ${printedBy}` : ""}
          </span>
        </footer>
      </main>
    </div>
  );
}
